import type { ObservabilitySlimOptions } from './declarations/Observability';
import { globalObservability } from './global';
import { buildDefaultDatadogOptions } from './internal/datadog';
import { getDeployEnv } from './internal/deployEnv';
import { loadGoogleTagManager } from './internal/googleTagManager';
import { BASE_JOURNEY_ENDPOINT, initJourney } from './internal/journey';
import { loadSegment } from './internal/segment';
import { buildDefaultSentryOpts } from './internal/sentry';
import { DebugObservability, RealObservability } from './observability-base';

/** Call this before you use useObservability or globalObservability. */
export function initObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
>({
  appName,
  env,
  version,
  eventTrackingCategory,
  sentry,
  datadog,
  debug,
  sendJourneyEventsInDev,
  journeyEndpoint,
  lazyLoadAnalytics,
  isBot,
}: ObservabilitySlimOptions<AppCategory>): void {
  if (globalObservability.isInitialized()) {
    throw new Error('initObservability was already called');
  }

  env = env || getDeployEnv();
  if (env === 'development') {
    let journeyEventClient: ReturnType<typeof initJourney>['eventClient'];
    if (sendJourneyEventsInDev) {
      // manually set the journey endpoint to staging when in dev mode
      const { eventClient } = initJourney(BASE_JOURNEY_ENDPOINT);
      journeyEventClient = eventClient;
    }
    globalObservability.setCurrentInstance(
      new DebugObservability<AppCategory, AppActions>(
        {
          log: debug,
          sendJourneyEventsInDev,
          journeyEventClient,
          env,
        },
        eventTrackingCategory,
      ),
    );
  } else {
    // TODO: Hide these in observability/internal like initSentry.
    // check if we're in the browser
    if (typeof window !== 'undefined' && !isBot) {
      loadGoogleTagManager(lazyLoadAnalytics);
      loadSegment(undefined, lazyLoadAnalytics);
    }

    const realObservabilityInstance = new RealObservability<AppCategory, AppActions>(
      eventTrackingCategory,
    );
    if (!isBot) {
      const { eventClient, userClient } = initJourney(journeyEndpoint);
      userClient && realObservabilityInstance.setJourneyUserClient(userClient);
      eventClient && realObservabilityInstance.setJourneyEventClient(eventClient);
    }
    globalObservability.setCurrentInstance(realObservabilityInstance);
    sentry
      ?.init({ ...buildDefaultSentryOpts({ appName, env, version }), ...sentry })
      .then((sentryClient) => {
        realObservabilityInstance.setSentryClient(sentryClient);
      })
      .catch((e) => {
        console.error('Error initializing Sentry', e);
      });
    datadog
      ?.init({ ...buildDefaultDatadogOptions({ appName, env, version }), ...datadog })
      .then((datadogRumClient) => {
        realObservabilityInstance.setDatadogRumClient(datadogRumClient);
      })
      .catch((e) => {
        console.error('Error initializing Datadog', e);
      });
  }
}

export { type ContextualData, type JourneyEventContextualData } from './contextualData';
export type { EventTrackingAction } from './declarations/EventTracking';
export type { EventData, EventName } from './declarations/JourneyEvent';
export type {
  DeployEnv,
  ObservabilitySlimOptions,
  ScopedObservability,
  TrackingOptions,
} from './declarations/Observability';
export { AddContextualData, useContextualData } from './react/ContextualDataContext';
export { useObservability } from './react/useObservability';
export { globalObservability } from './global';
export { addDatadogRUMGlobalContext, DebugObservability, testonly } from './observability-base';
export { analytics } from './analytics';
export { setRUMUser, setRUMUserProperty } from './internal/datadog';
export { subscribeToDevtoolsEvents, type DevtoolsEvent } from './devtools-events';
